/**
 * ReferenceItem component for displaying URL previews in a card format (RN + Web)
 */
import React, {useEffect, useState} from 'react';
import styled, {css, useTheme} from 'styled-components/native';
import {View, Image, Platform, ActivityIndicator} from 'react-native';
import Animated, {FadeIn, FadeOut} from 'react-native-reanimated';
import parseMetadata from '../../utils/metadata';
import {Metadata} from '../../interfaces/components/linkTooltip';
import Text from '../textGeneric';
import {openUrl} from '../../utils/helpers';

interface ReferenceCardProps {
  visibleItems: number;
  screenWidth: number;
  contentPadding: number;
  itemGap: number;
}

const ReferenceCard = styled.TouchableOpacity<ReferenceCardProps>`
  width: ${({visibleItems, screenWidth, contentPadding, itemGap}) =>
    (screenWidth - itemGap * (visibleItems - 1) - contentPadding * 2) /
    visibleItems}px;
  height: 120px;
  background-color: ${({theme}) => theme.text.light + '77'};
  border: 1px solid ${({theme}) => theme.secondary + '44'};
  border-radius: 12px;
  padding: 16px;
`;

const IconWrapper = styled.View`
  width: 20px;
  height: 20px;
  border-radius: 10px;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

const ReferenceIcon = styled(Image)<{$isHidden: boolean}>`
  width: 20px;
  height: 20px;
  border-radius: 10px;
  ${({$isHidden}) =>
    $isHidden &&
    css`
      display: none;
    `}
`;

const ReferenceDomain = styled(Text)`
  color: ${({theme}) => theme.text.dark + 'AA'};
  font-size: ${({theme}) => theme.text.s9}px;
  flex: 1;
`;

const ReferenceTitle = styled(Text)`
  font-size: ${({theme}) => theme.text.s8}px;
  margin-top: 8px;
  color: ${({theme}) => theme.text.dark};
`;

interface ReferenceItemProps {
  href: string;
  visibleItems: number;
  screenWidth: number;
  contentPadding: number;
  itemGap: number;
  onLoad?: () => void;
  disabled?: boolean;
}

const extractDomain = (url: string) => {
  try {
    const hostname = new URL(url).hostname;
    return hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
};

const getFavIcon = (url: string) => {
  const domain = extractDomain(url);
  return `https://www.google.com/s2/favicons?sz=32&domain=${domain}`;
};

const ReferenceItem: React.FC<ReferenceItemProps> = ({
  href,
  visibleItems,
  screenWidth,
  contentPadding,
  itemGap,
  onLoad,
  disabled,
}) => {
  const theme = useTheme();
  const domain = extractDomain(href);
  const [metadata, setMetadata] = useState<Metadata>({
    title: '',
    description: '',
    domain: '',
    favIcon: '',
  });
  const [iconUri, setIconUri] = useState<string>(getFavIcon(href));
  const [iconLoading, setIconLoading] = useState<boolean>(true);

  const handleOpen = () => {
    if (disabled) return;
    openUrl(href);
  };

  useEffect(() => {
    (async () => {
      try {
        const res = await parseMetadata(href);
        setMetadata({
          title: res.title || res.ogTitle || '',
          description: res.description || res.ogDescription || '',
          domain,
          favIcon: res.favicon,
        });
        if (res.favicon) {
          setIconUri(res.favicon);
          setIconLoading(true);
        }
      } catch {
        setMetadata({title: '', description: '', domain, favIcon: ''});
      } finally {
        onLoad && onLoad();
      }
    })();
  }, [href]);

  return (
    <Animated.View entering={FadeIn} exiting={FadeOut}>
      <ReferenceCard
        activeOpacity={disabled ? 1 : 0.8}
        onPress={handleOpen}
        disabled={!!disabled}
        visibleItems={visibleItems}
        screenWidth={screenWidth}
        contentPadding={contentPadding}
        itemGap={itemGap}>
        <View style={{flexDirection: 'row', alignItems: 'center', gap: 8}}>
          <IconWrapper>
            {iconLoading && (
              <ActivityIndicator
                size={Platform.OS === 'ios' ? 'small' : 14}
                color={theme.primary}
              />
            )}
            <ReferenceIcon
              $isHidden={iconLoading}
              source={{uri: iconUri}}
              onLoadEnd={() => setIconLoading(false)}
              onError={() => {
                setIconUri(getFavIcon(href));
                setIconLoading(false);
              }}
            />
          </IconWrapper>
          <ReferenceDomain numberOfLines={1}>{domain}</ReferenceDomain>
        </View>
        <ReferenceTitle bold numberOfLines={2}>
          {metadata.title || href}
        </ReferenceTitle>
      </ReferenceCard>
    </Animated.View>
  );
};

export default ReferenceItem;
